/**
 * React Query hooks for Fund LPA Terms
 *
 * These hooks provide CRUD operations for fund LPA terms,
 * including create, read, update, and delete operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fundService } from '@/services/fundService';
import type {
  FundLPATerms,
  FundLPATermsCreate,
  FundLPATermsUpdate,
} from '@/types/lpa-terms';

// Query key constants
const FUND_LPA_TERMS_QUERY_KEYS = {
  all: ['fund-lpa-terms'] as const,
  byFundId: (fundId: string) => ['fund-lpa-terms', fundId] as const,
};

/**
 * Hook to fetch LPA terms for a specific fund
 */
export function useFundLPATerms(fundId: string | undefined) {
  return useQuery<FundLPATerms, Error>({
    queryKey: fundId ? FUND_LPA_TERMS_QUERY_KEYS.byFundId(fundId) : [],
    queryFn: () => fundService.getFundLPATerms(fundId!),
    enabled: !!fundId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook to create LPA terms for a fund
 */
export function useCreateFundLPATerms() {
  const queryClient = useQueryClient();

  return useMutation<FundLPATerms, Error, FundLPATermsCreate>({
    mutationFn: (lpaTermsData: FundLPATermsCreate) =>
      fundService.createFundLPATerms(lpaTermsData),
    onSuccess: (data) => {
      // Update the cache with the new LPA terms
      queryClient.setQueryData(
        FUND_LPA_TERMS_QUERY_KEYS.byFundId(data.fund_id),
        data
      );

      // Invalidate the fund details to update any related data
      queryClient.invalidateQueries({
        queryKey: ['funds', data.fund_id],
      });
    },
  });
}

/**
 * Hook to update LPA terms for a fund
 */
export function useUpdateFundLPATerms() {
  const queryClient = useQueryClient();

  return useMutation<
    FundLPATerms,
    Error,
    { fundId: string; data: FundLPATermsUpdate }
  >({
    mutationFn: ({ fundId, data }) =>
      fundService.updateFundLPATerms(fundId, data),
    onSuccess: (data) => {
      // Update the cache with the updated LPA terms
      queryClient.setQueryData(
        FUND_LPA_TERMS_QUERY_KEYS.byFundId(data.fund_id),
        data
      );

      // Invalidate the fund details to update any related data
      queryClient.invalidateQueries({
        queryKey: ['funds', data.fund_id],
      });
    },
  });
}

/**
 * Hook to delete LPA terms for a fund
 */
export function useDeleteFundLPATerms() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (fundId: string) => fundService.deleteFundLPATerms(fundId),
    onSuccess: (_, fundId) => {
      // Remove the LPA terms from the cache
      queryClient.removeQueries({
        queryKey: FUND_LPA_TERMS_QUERY_KEYS.byFundId(fundId),
      });

      // Invalidate the fund details to update any related data
      queryClient.invalidateQueries({
        queryKey: ['funds', fundId],
      });
    },
  });
}
